import {ItemType} from 'antd/es/menu/interface';
import {matchPath} from 'react-router-dom';

export function getIsUserAvailable(isWgisSupportUser: boolean): boolean {
    return !isWgisSupportUser;
}

function getMenuItemFlatKeys(
    menuItems: Array<ItemType>,
    parentKeys?: Array<React.Key>
): Array<[React.Key, Array<React.Key>]> {
    return menuItems.flatMap((item) => {
        if (!item || !item.key) {
            return [];
        }

        const itemKeysPath = [...(parentKeys ?? []), item.key];

        return [
            [item.key, itemKeysPath],
            ...(item.type === 'submenu' && item.children ? getMenuItemFlatKeys(item.children, itemKeysPath) : []),
        ];
    });
}

function menuKeyToRoutePattern(key: React.Key): string | null {
    if (typeof key === 'string') {
        return key;
    }

    if (typeof key === 'number') {
        return String(key);
    }

    return null;
}

/** Higher score = match before others (more static segments, longer path). */
function routePatternSpecificity(pattern: string): number {
    const parts = pattern.split('/').filter(Boolean);
    const base = parts.length * 100;

    return (
        base +
        parts.reduce((accumulator, part) => {
            if (part.startsWith(':')) {
                return accumulator - 5;
            }

            if (part === '*') {
                return accumulator - 50;
            }

            return accumulator + 25;
        }, 0)
    );
}

type PartitionedMenuRoutesType = {
    pathnameLiteralHit: Array<string> | null;
    exactStatic: Map<string, Array<React.Key>>;
    parameterPatterns: Array<{pattern: string; keyPath: Array<React.Key>}>;
    staticPrefixRows: Array<{pattern: string; keyPath: Array<React.Key>}>;
};

function partitionMenuRoutesForPathname(
    flat: Array<[React.Key, Array<React.Key>]>,
    pathname: string
): PartitionedMenuRoutesType {
    return flat.reduce<PartitionedMenuRoutesType>(
        (accumulator, [key, keyPath]) => {
            const pattern = menuKeyToRoutePattern(key);

            if (!pattern) {
                return accumulator;
            }

            if (pattern === pathname) {
                if (accumulator.pathnameLiteralHit) {
                    return accumulator;
                }

                return {
                    ...accumulator,
                    pathnameLiteralHit: keyPath.map(String),
                };
            }

            if (!pattern.startsWith('/')) {
                return accumulator;
            }

            if (pattern.includes(':')) {
                return {
                    ...accumulator,
                    parameterPatterns: [...accumulator.parameterPatterns, {pattern, keyPath}],
                };
            }

            const nextExactStatic = accumulator.exactStatic.has(pattern)
                ? accumulator.exactStatic
                : new Map(accumulator.exactStatic).set(pattern, keyPath);

            return {
                ...accumulator,
                exactStatic: nextExactStatic,
                staticPrefixRows: [...accumulator.staticPrefixRows, {pattern, keyPath}],
            };
        },
        {
            pathnameLiteralHit: null,
            exactStatic: new Map(),
            parameterPatterns: [],
            staticPrefixRows: [],
        }
    );
}

function firstMatchingParameterPattern(
    pathname: string,
    parameterPatterns: Array<{pattern: string; keyPath: Array<React.Key>}>,
    exact: boolean
): Array<string> | null {
    const sorted = [...parameterPatterns].sort(
        (left, right) => routePatternSpecificity(right.pattern) - routePatternSpecificity(left.pattern)
    );

    const found = sorted.find((row) => matchPath(pathname, {exact, path: row.pattern}));

    return found ? found.keyPath.map(String) : null;
}

function firstMatchingStaticPrefix(
    pathname: string,
    staticPrefixRows: Array<{pattern: string; keyPath: Array<React.Key>}>
): Array<string> | null {
    const byPattern = staticPrefixRows.reduce<Map<string, Array<React.Key>>>((map, row) => {
        if (!map.has(row.pattern)) {
            map.set(row.pattern, row.keyPath);
        }

        return map;
    }, new Map());

    const sortedPatterns = [...byPattern.keys()].sort((left, right) => right.length - left.length);

    const matchedPattern = sortedPatterns.find((pattern) => matchPath(pathname, {exact: false, path: pattern}));

    if (!matchedPattern) {
        return null;
    }

    const keyPathForMatch = byPattern.get(matchedPattern);

    return keyPathForMatch ? keyPathForMatch.map(String) : null;
}

/**
 * Resolves Ant Design Menu `selectedKeys` (full key path) for the current URL.
 * Supports React Router path patterns in menu keys (e.g. `/company-matching/:providerId/...`).
 */
export function getSelectedKeysForPathname(pathname: string, menuItems: Array<ItemType>): Array<string> {
    const flat = getMenuItemFlatKeys(menuItems);
    const partitioned = partitionMenuRoutesForPathname(flat, pathname);

    if (partitioned.pathnameLiteralHit) {
        return partitioned.pathnameLiteralHit;
    }

    const exactStaticHit = partitioned.exactStatic.get(pathname);

    if (exactStaticHit) {
        return exactStaticHit.map(String);
    }

    return (
        firstMatchingParameterPattern(pathname, partitioned.parameterPatterns, true) ??
        firstMatchingParameterPattern(pathname, partitioned.parameterPatterns, false) ??
        firstMatchingStaticPrefix(pathname, partitioned.staticPrefixRows) ??
        []
    );
}
